import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import './pdp.css';
import { getStoredTokens } from '../../../adapters/api-client/tokenStorage';
import { fetchCatalogDetail } from '../api/catalog.api';
import { initiateContact } from '../api/contact.api';
import { saveListing, unsaveListing } from '../api/saved-listings.api';
import { ReportButton } from '../components/ReportButton';
import {
  BUYER_NAV,
  CameraIcon,
  CheckIcon,
  CircleIcon,
  ColorRail,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';
import { VehicleDetailVM } from '../types';

/**
 * CatalogDetail (T0 Epic A.5, W02 v3 fidelity) — connected public PDP.
 *
 * Mobile-first rebuild matching the W02 prototype's top: detail topbar over a
 * full-bleed hero, rounded content sheet, header block (price + chips + title +
 * 4-spec grid), expanded trust list, derived "what's special" tags, disclosure,
 * and a sticky contact CTA. All driven by live /catalog data.
 */

const FALLBACK_IMG =
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1000&h=750&fit=crop&q=85';
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const rd = (n: number) => `RD$ ${Math.round(n * 58.5).toLocaleString('en-US')}`;
const km = (n: number) => n.toLocaleString('en-US');

const heroImage = (vm: VehicleDetailVM): string => {
  const url = vm.photos[0]?.url;
  return url && !url.includes('X-Amz-Stub') ? url : FALLBACK_IMG;
};

const TRUST_META: Record<string, string> = {
  verificado: 'Identidad del vendedor',
  carfax: 'Historial del VIN',
  tasado: 'Precio de referencia',
  inspected: 'Inspección de socio',
};

const deriveTags = (vm: VehicleDetailVM, verdict: string): string[] => {
  const tags: string[] = [];
  if (verdict === 'good') tags.push('Bajo mercado');
  if (vm.specs.vinState === 'verified') tags.push('VIN verificado');
  if (vm.trust.disclosureProvided) tags.push('Con declaración');
  if (vm.trust.photoComplete) tags.push('Fotos completas');
  if (vm.specs.mileageKm < 60000) tags.push('Bajo kilometraje');
  return tags;
};

export const CatalogDetail: React.FC = () => {
  const authChrome = useAuthChrome();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vm, setVm] = useState<VehicleDetailVM | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [contactState, setContactState] = useState<'idle' | 'submitting' | 'success' | 'failed'>(
    'idle',
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(undefined);
    fetchCatalogDetail(id)
      .then((d) => active && setVm(d))
      .catch(
        (err) => active && setError(err instanceof Error ? err.message : 'Listado no disponible.'),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  const requireAuth = (): boolean => {
    if (!getStoredTokens().idToken) {
      navigate('/login');
      return false;
    }
    return true;
  };

  const handleContact = async () => {
    if (!id || !requireAuth()) return;
    setContactState('submitting');
    try {
      const res = await initiateContact(id, 'WHATSAPP');
      setContactState('success');
      if (res.whatsappUrl) window.open(res.whatsappUrl, '_blank', 'noopener,noreferrer');
    } catch {
      setContactState('failed');
    }
  };

  const toggleSave = async () => {
    if (!id || !requireAuth()) return;
    const next = !saved;
    setSaved(next);
    try {
      await (next ? saveListing(id) : unsaveListing(id));
    } catch {
      setSaved(!next);
    }
  };

  if (loading) return <div className="cia-pdp-state">Cargando…</div>;
  if (error || !vm)
    return (
      <div className="cia-pdp-state" data-testid="vehicle-detail-error">
        <p role="alert">{error ?? 'Listado no disponible.'}</p>
        <button type="button" className="cia-pdp-cta-msg" onClick={() => navigate('/search')}>
          Volver al catálogo
        </button>
      </div>
    );

  const { specs, trust } = vm;
  const verdict =
    vm.suggestedPrice && vm.sellerPrice <= vm.suggestedPrice * 0.97
      ? 'good'
      : vm.suggestedPrice && vm.sellerPrice >= vm.suggestedPrice * 1.03
      ? 'over'
      : 'fair';
  const marks = trust.marks ?? [];
  // DEF-005: this used to render granted÷total as a percentage — e.g.
  // "100% confianza" — which reads as the 0-100 Trust Score. That engine is
  // BC-03-E05 / BC-07-E09, both deferred T1, and two of the four marks are
  // granted by hand through the admin endpoints, so a listing could show
  // "100% confianza" because an operator clicked twice. Report the plain
  // count of granted signals instead; no score is implied.
  const trustGranted = marks.filter((m) => m.on).length;
  const tags = deriveTags(vm, verdict);
  const ctaLabel =
    contactState === 'submitting'
      ? 'Enviando…'
      : contactState === 'success'
      ? '¡Contacto enviado!'
      : contactState === 'failed'
      ? 'Reintentar'
      : 'Contactar al vendedor';

  return (
    <div className="cia-pdp" data-testid="vehicle-detail">
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />

      {/* Mobile: one column, ordered by CSS. Desktop (>=1024px): a real
          two-column grid — main + 372px sidebar — via the prototype's
          display:contents technique (car-ia-ficha-completa.html). */}
      <div className="cia-pdp-app">
        <div className="cia-pdp-col cia-pdp-col--main">
          <div className="cia-pdp-topbar">
            <button
              className="cia-pdp-icon"
              aria-label="Volver"
              onClick={() => navigate('/search')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="cia-pdp-actions">
              <button
                className={`cia-pdp-icon${saved ? ' cia-pdp-icon--on' : ''}`}
                aria-label="Guardar"
                aria-pressed={saved}
                onClick={toggleSave}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill={saved ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z" />
                </svg>
              </button>
            </div>
          </div>

          <div
            className="cia-pdp-hero"
            style={{ backgroundImage: `url('${heroImage(vm)}')` }}
            role="img"
            aria-label={vm.title}
          >
            <span className="cia-pdp-counter">
              <CameraIcon size={13} />
              {vm.photos.length}
            </span>
          </div>
        </div>

        <div className="cia-pdp-col cia-pdp-col--side">
          <section className="cia-pdp-head">
            <div className="cia-pdp-headchips">
              {verdict === 'good' && (
                <span className="cia-pdp-headchip cia-pdp-headchip--good">Buen precio</span>
              )}
              {verdict === 'over' && (
                <span className="cia-pdp-headchip cia-pdp-headchip--warn">Sobre mercado</span>
              )}
              {vm.suggestedPrice != null && (
                <span className="cia-pdp-headchip cia-pdp-headchip--tasador">
                  Tasador {usd(vm.suggestedPrice)}
                </span>
              )}
            </div>
            <div className="cia-pdp-price-row">
              <span className="cia-pdp-price-usd">{usd(vm.sellerPrice)}</span>
              <span className="cia-pdp-price-rd">{rd(vm.sellerPrice)}</span>
            </div>
            <h1 className="cia-pdp-title">
              {specs.make} {specs.model}
            </h1>
            <div className="cia-pdp-trim">
              {specs.year} · {specs.transmission} · {specs.fuel} · {specs.color}
            </div>
            <div className="cia-pdp-specgrid">
              <div className="cia-pdp-spec">
                <span className="cia-pdp-spec-value">{specs.year}</span>
                <span className="cia-pdp-spec-label">Año</span>
              </div>
              <div className="cia-pdp-spec">
                <span className="cia-pdp-spec-value">{km(specs.mileageKm)}</span>
                <span className="cia-pdp-spec-label">Km</span>
              </div>
              <div className="cia-pdp-spec">
                <span className="cia-pdp-spec-value">{specs.fuel}</span>
                <span className="cia-pdp-spec-label">Combustible</span>
              </div>
              <div className="cia-pdp-spec">
                <span className="cia-pdp-spec-value">{specs.transmission}</span>
                <span className="cia-pdp-spec-label">Transmisión</span>
              </div>
            </div>
            <div className="cia-pdp-sidecta">
              <button
                className="cia-pdp-cta-main"
                onClick={handleContact}
                disabled={contactState === 'submitting' || contactState === 'success'}
              >
                {ctaLabel}
              </button>
              <button className="cia-pdp-cta-msg" onClick={handleContact}>
                Mensaje
              </button>
            </div>
          </section>
        </div>

        <div className="cia-pdp-col cia-pdp-col--secs">
          {marks.length > 0 && (
            <section className="cia-pdp-sec">
              <div className="cia-pdp-sec-title">
                Confianza
                <span className="cia-pdp-sec-pct">
                  {trustGranted}/{marks.length} señales
                </span>
              </div>
              <div className="cia-pdp-trust">
                {marks.map((m) => (
                  <div
                    key={m.key}
                    className={`cia-pdp-trust-item cia-pdp-trust-item--${m.on ? 'on' : 'off'}`}
                  >
                    <span className="cia-pdp-trust-ic">
                      {m.on ? <CheckIcon size={18} /> : <CircleIcon size={18} />}
                    </span>
                    <span>
                      <span className="cia-pdp-trust-label">{m.label}</span>
                      <span className="cia-pdp-trust-meta">
                        {m.on ? TRUST_META[m.key] ?? '' : 'No disponible'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tags.length > 0 && (
            <section className="cia-pdp-sec">
              <div className="cia-pdp-sec-title">Lo especial</div>
              <div className="cia-pdp-tags">
                {tags.map((t) => (
                  <span key={t} className="cia-pdp-tag">
                    {t}
                  </span>
                ))}
              </div>
            </section>
          )}

          {vm.disclosureText && (
            <section className="cia-pdp-sec">
              <div className="cia-pdp-sec-title">Declaración del vendedor</div>
              <p className="cia-pdp-disclosure">{vm.disclosureText}</p>
            </section>
          )}

          <section className="cia-pdp-sec">
            <ReportButton vehicleId={vm.id} onRequireLogin={() => navigate('/login')} />
          </section>
        </div>
      </div>

      <div className="cia-pdp-cta">
        <button className="cia-pdp-cta-msg" onClick={handleContact}>
          Mensaje
        </button>
        <button
          className="cia-pdp-cta-main"
          onClick={handleContact}
          disabled={contactState === 'submitting' || contactState === 'success'}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default CatalogDetail;
